import { isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, OnInit, Output, PLATFORM_ID } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { I18nService } from '@personal-area/global/i18n';
import { LOCAL_STORAGE } from '@personal-area/storage';

const closedKey = 'old-browser-closed';

class Browser {
  key: string;
  value: string;
  version: number;
}

@Component({
    selector: 'app-old-browser',
    imports: [
        TranslateModule,
    ],
    templateUrl: './old-browser.component.html',
    styleUrls: ['./old-browser.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OldBrowserComponent implements OnInit {
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _localStorage = inject(LOCAL_STORAGE);

  @Output() public closed = new EventEmitter<void>();

  public isOldBrowser = false;

  private readonly _browsers: Browser[];

  constructor(private readonly _i18nService: I18nService) {
    this._browsers = [
      {
        key: 'Edge',
        value: 'edge',
        version: 18,
      },
      {
        key: 'Trident',
        value: 'ie',
        version: 8,
      },
      {
        key: 'OPR',
        value: 'opera',
        version: 66,
      },
      {
        key: 'Opera',
        value: 'opera_old',
        version: 42,
      },
      {
        key: 'coc_coc_browser',
        value: 'coccoc',
        version: 63,
      },
      {
        key: 'Maxthon',
        value: 'maxthon',
        version: 5,
      },
      {
        key: 'UCBrowser',
        value: 'ucbrowser',
        version: 12,
      },
      {
        key: 'Chrome',
        value: 'chrome',
        version: 54,
      },
      {
        key: 'Safari',
        value: 'safari',
        version: 10,
      },
      {
        key: 'Firefox',
        value: 'firefox',
        version: 66,
      },
    ];
  }

  ngOnInit(): void {
    const userAgent = navigator.userAgent;
    const closed = this._localStorage.getItem(closedKey) === '1';

    if (isPlatformServer(this._platformId) || !userAgent || closed) {
      if (closed) {
        this.closed.emit();
      }
      return;
    }

    // Detect current browser requirements
    const config = this._browsers.find((b) => {
      return userAgent.search(new RegExp(b.key)) !== -1;
    });

    if (config) {
      const version = this._currentVersion(userAgent, config.value);

      this.isOldBrowser = version && version < config.version;
    }

    if (!this.isOldBrowser) {
      this.closed.emit();
    }

  }

  get languageA2(): string {
    return this._i18nService.languageA2.toLowerCase();
  }

  close(): void {
    this._localStorage.setItem(closedKey, '1');
    this.isOldBrowser = false;
    this.closed.emit();
  }

  /**
   * Find current browser version
   * @param userAgent
   * @param browserTag
   * @return number|null
   */
  private _currentVersion(userAgent: string, browserTag: string) {
    let versionNum = 0;

    if (browserTag) {
      const version =
        (function(b: string) {
          switch (b) {
            case 'ie':
              return userAgent.split('Trident/')[1].split(';')[0];
            case 'firefox':
              return userAgent.split('Firefox/')[1];
            case 'opera':
              return userAgent.split('OPR/')[1];
            case 'opera_old':
              return userAgent.split('Opera/')[1];
            case 'chrome':
              return userAgent.split('Chrome/').length > 1 ? userAgent.split('Chrome/')[1].split(' ')[0] : '';
            case 'safari':
              return userAgent.split('Version/').length > 1 ? userAgent.split('Version/')[1].split(' ')[0] : '';
            case 'coccoc':
              return userAgent.split('coc_coc_browser/')[1].split(' ')[0];
            case 'edge':
              return userAgent.split('Edge/')[1];
            case 'maxthon':
              return userAgent.split('Maxthon/')[1].split(' ')[0];
            case 'ucbrowser':
              return userAgent.split('UCBrowser/')[1].split(' ')[0];
          }
        })(browserTag) || '';

      versionNum = +version.split('.')[0];
    } else {
      return null;
    }
    return versionNum;
  }
}
